import {
  formatVehicleMarketSourceTypeLabel,
} from "../../../../constants/vehicleMarketSourceConstants.js";

/** @deprecated Use formatVehicleMarketSourceTypeLabel — kept for grid compatibility. */
export const formatSourceTypeLabel = formatVehicleMarketSourceTypeLabel;

/** Paces used by Vehicle Market ingest, including the production cadences stored on sources. */
export const VEHICLE_MARKET_SCHEDULE_INTERVALS = [
  { label: "Only when I start it", minutes: null },
  { label: "Every 30 minutes", minutes: 30 },
  { label: "Every 1 hour", minutes: 60 },
  { label: "Every 3 hours", minutes: 180 },
  { label: "Every 5 hours", minutes: 300 },
  { label: "Every 6 hours", minutes: 360 },
  { label: "Every 7 hours", minutes: 420 },
  { label: "Every 8 hours", minutes: 480 },
  { label: "Every 9 hours", minutes: 540 },
  { label: "Every 10 hours", minutes: 600 },
  { label: "Every 11 hours", minutes: 660 },
  { label: "Every 12 hours", minutes: 720 },
  { label: "Daily", minutes: 1440 },
  { label: "Weekly", minutes: 10080 },
];

const SCHEDULE_INTERVAL_LABELS = VEHICLE_MARKET_SCHEDULE_INTERVALS;

export const VEHICLE_MARKET_REGISTRY_LABELS = {
  paired: "Paired",
  automotive_only: "Ingest only",
  vehicle_only: "Inventory only",
};

export const VEHICLE_MARKET_REGISTRY_TOOLTIPS = {
  paired: "Production ingest source joined with vehicle compliance record",
  automotive_only: "Production ingest source without a matching vehicle compliance record",
  vehicle_only:
    "This source exists in the vehicle compliance/discovery registry but does not have production ingest configured.",
};

export const VEHICLE_ONLY_PRODUCTION_TOOLTIP =
  "Production ingest is not configured for this source. Register production ingest before scheduling.";

export const VEHICLE_ONLY_RUN_DISABLED_TOOLTIP =
  "Production ingest is not configured. Register automotive ingest before running this source.";

export const VEHICLE_ONLY_SCHEDULE_DISABLED_TOOLTIP =
  "Schedule is unavailable because this source does not have production ingest configured.";

export const VEHICLE_ONLY_DISCOVERY_DISABLED_TOOLTIP =
  "Discovery route is not configured for this source.";

export function isVehicleOnlyRow(row) {
  return row?.registry === "vehicle_only";
}

export function deriveRegistryBadge(row) {
  if (!isVehicleOnlyRow(row)) {
    return {
      label: VEHICLE_MARKET_REGISTRY_LABELS[row?.registry] || row?.registry || "—",
      tooltip: VEHICLE_MARKET_REGISTRY_TOOLTIPS[row?.registry] || "",
    };
  }

  const hasDiscovery =
    row?.capabilities?.supportsDiscovery || row?.capabilities?.supportsDetailFetch;

  return {
    label: hasDiscovery ? "Inventory only" : "Compliance only",
    tooltip: VEHICLE_MARKET_REGISTRY_TOOLTIPS.vehicle_only,
  };
}

export function deriveVehicleOnlyStatusKey(row) {
  if (!isVehicleOnlyRow(row)) {
    return null;
  }

  const catalogStatuses = new Set([
    "inventory_only",
    "inventory_active",
    "planning_only",
    "compliance_only",
    "discovery_only",
    "compliance_review",
    "needs_ingest_registration",
    "partner_access_required",
    "blocked",
    "deferred",
    "retired",
  ]);

  if (catalogStatuses.has(row.healthStatus)) {
    return row.healthStatus;
  }

  if (row.capabilities?.supportsDiscovery) {
    return "discovery_only";
  }

  const reviews = [
    row?.robotsReviewStatus,
    row?.termsReviewStatus,
    row?.accessReviewStatus,
  ].filter(Boolean);

  const normalizedReviews = reviews.map(normalizeReviewStatus);
  const needsReview = normalizedReviews.some((status) =>
    ["not_reviewed", "review_pending", "unknown"].includes(status)
  );
  const blockedReview = normalizedReviews.some((status) => status === "blocked");
  const partnerAccess = hasNeedsPartnerAccess(reviews);
  const withLimits = hasApprovedWithLimits(reviews);

  if (needsReview) {
    return "compliance_review";
  }

  if (blockedReview || normalizeReviewStatus(row?.sourceStatus) === "blocked") {
    return "blocked";
  }

  if (partnerAccess) {
    return "partner_access_required";
  }

  if (row?.allowedToIngest === false && withLimits) {
    return row?.isActive ? "inventory_active" : "planning_only";
  }

  if (!row?.capabilities?.supportsDiscovery && !row?.capabilities?.supportsDetailFetch) {
    return "needs_ingest_registration";
  }

  return row?.isActive ? "inventory_active" : "inventory_only";
}

const EXCLUSION_HEALTH_KEYS = new Set([
  "blocked",
  "retired",
  "deferred",
  "partner_feed",
  "partner_access_required",
  "reference_only",
  "planning_only",
  "do_not_use",
  "inventory_active",
  "wiring_in_progress",
  "paired_adapter_pending",
  "manual_only",
  "manual_ingest_validated",
  "manual_ingest_validated_with_warnings",
]);

export function deriveManualIngestStatusKey(row) {
  if (!row?.ingestValidationStatus) return null;
  if (row.ingestValidationStatus === "validated_with_warnings") {
    return "manual_ingest_validated_with_warnings";
  }
  if (row.ingestValidationStatus === "validated") {
    return "manual_ingest_validated";
  }
  return null;
}

export function deriveOperatorStatusKey(row) {
  const manualIngestKey = deriveManualIngestStatusKey(row);
  if (manualIngestKey) {
    return manualIngestKey;
  }
  if (row?.displayHealthStatus && EXCLUSION_HEALTH_KEYS.has(row.displayHealthStatus)) {
    return row.displayHealthStatus;
  }
  if (row?.exclusionDisposition && EXCLUSION_HEALTH_KEYS.has(row.exclusionDisposition)) {
    return row.exclusionDisposition;
  }
  return null;
}

export function normalizeVehicleMarketHealthKey(row) {
  const operatorKey = deriveOperatorStatusKey(row);
  if (operatorKey) {
    return operatorKey;
  }

  if (isVehicleOnlyRow(row)) {
    return deriveVehicleOnlyStatusKey(row);
  }

  if (row?.complianceBlocker && row.complianceBlocker !== "none") {
    return "blocked";
  }
  if (!row?.isActive && row?.isActive !== null) {
    return "disabled";
  }
  if (row?.isRunning || row?.healthStatus === "running" || row?.lastRunStatus === "running") {
    return "running";
  }
  if (row?.healthStatus === "failed" || row?.lastRunStatus === "failed") {
    return "failed";
  }
  if (row?.healthStatus === "healthy") {
    return "healthy";
  }
  return row?.healthStatus || "unknown";
}

function normalizeReviewStatus(status) {
  return status ? String(status).toLowerCase() : "";
}

function hasApprovedWithLimits(reviews) {
  return reviews.some((status) =>
    ["approved_with_limits", "approved with limits"].includes(normalizeReviewStatus(status))
  );
}

function hasNeedsPartnerAccess(reviews) {
  return reviews.some((status) =>
    ["needs_partner_access", "needs partner access"].includes(normalizeReviewStatus(status))
  );
}

function buildReviewTooltip(row, reviews) {
  return [
    row?.reviewSummary,
    row?.robotsReviewStatus ? `Robots: ${row.robotsReviewStatus}` : null,
    row?.termsReviewStatus ? `Terms: ${row.termsReviewStatus}` : null,
    row?.accessReviewStatus ? `Access: ${row.accessReviewStatus}` : null,
    row?.complianceNotes ? String(row.complianceNotes).slice(0, 160) : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function alignmentTone(state) {
  switch (state) {
    case "aligned_approved":
      return "confirmed";
    case "vehicle_approved_automotive_pending":
    case "vehicle_approved_automotive_limited":
      return "warning";
    case "vehicle_approved_automotive_blocked":
    case "blocked":
    case "retired":
      return "disputed";
    default:
      return "neutral";
  }
}

function buildPairedAlignmentTooltip(row) {
  return [
    row?.allowedToIngest === true ? "Vehicle overlay: allowed" : "Vehicle overlay: blocked",
    row?.licenseStatus ? `Automotive license: ${row.licenseStatus}` : null,
    row?.productionUsageScope ? `Usage: ${row.productionUsageScope}` : null,
    row?.publicDisplayAllowed ? `Public display: ${row.publicDisplayAllowed}` : null,
    row?.scheduleAllowed ? `Schedule eligible: ${row.scheduleAllowed}` : null,
    row?.attributionRequired ? "Attribution required" : null,
    row?.credentialsRequired && !row?.credentialsConfigured ? "Credentials missing" : null,
    row?.complianceBlocker && row.complianceBlocker !== "none"
      ? `Blocker: ${row.complianceBlocker}`
      : null,
    row?.automotiveNextAction,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function derivePairedAlignmentDisplay(row) {
  if (row?.registry !== "paired" || !row?.alignmentLabel) {
    return null;
  }
  return {
    label: row.alignmentLabel,
    tone: alignmentTone(row.alignmentState),
    tooltip: buildPairedAlignmentTooltip(row),
  };
}

export function deriveComplianceDisplay(row) {
  if (row?.registry === "paired" && row?.alignmentLabel) {
    const alignment = derivePairedAlignmentDisplay(row);
    if (alignment) {
      return alignment;
    }
  }

  if (!row?.vehicleSourceId && row?.registry === "automotive_only") {
    return { label: "Unknown", tone: "neutral", tooltip: "No vehicle compliance overlay for this source." };
  }

  const reviews = [
    row?.robotsReviewStatus,
    row?.termsReviewStatus,
    row?.accessReviewStatus,
  ].filter(Boolean);

  const normalizedReviews = reviews.map(normalizeReviewStatus);
  const needsReview = normalizedReviews.some((status) =>
    ["not_reviewed", "review_pending", "unknown"].includes(status)
  );
  const blockedReview = normalizedReviews.some((status) => status === "blocked");
  const partnerAccess = hasNeedsPartnerAccess(reviews);
  const withLimits = hasApprovedWithLimits(reviews);

  if (isVehicleOnlyRow(row)) {
    if (blockedReview || row?.sourceStatus === "BLOCKED") {
      return {
        label: "Blocked",
        tone: "disputed",
        tooltip: row?.blockedReason || buildReviewTooltip(row, reviews),
      };
    }

    if (partnerAccess) {
      return {
        label: "Needs partner access",
        tone: "warning",
        tooltip: buildReviewTooltip(row, reviews) || "Partner feed or permitted access required.",
      };
    }

    if (row?.allowedToIngest === false && (needsReview || !reviews.length)) {
      return {
        label: "Compliance review",
        tone: "warning",
        tooltip: row?.reviewSummary || "Not allowed to ingest until compliance review completes.",
      };
    }

    if (needsReview) {
      return {
        label: "Needs review",
        tone: "warning",
        tooltip: buildReviewTooltip(row, reviews),
      };
    }

    if (row?.allowedToIngest === true && withLimits) {
      return {
        label: "Allowed with limits",
        tone: "confirmed",
        tooltip:
          buildReviewTooltip(row, reviews) ||
          (row?.productionIngestConfigured
            ? "Compliance approved with documented limits. Manual ingest configured."
            : "Compliance approved with documented limits. Production ingest not configured."),
      };
    }

    if (row?.allowedToIngest === true) {
      return {
        label: "Allowed",
        tone: "confirmed",
        tooltip: "Compliance gates satisfied. Production ingest still requires registration.",
      };
    }

    return {
      label: "Not wired",
      tone: "neutral",
      tooltip: "Inventory/compliance record only — no production ingest path configured.",
    };
  }

  const blocker = row?.complianceBlocker && row.complianceBlocker !== "none" ? row.complianceBlocker : null;

  if (blockedReview || (row?.allowedToIngest === false && blocker)) {
    return {
      label: "Blocked",
      tone: "disputed",
      tooltip: [
        blocker ? `Blocker: ${blocker}` : null,
        row?.allowedToIngest === false ? "Not allowed to ingest" : null,
        buildReviewTooltip(row, reviews),
      ]
        .filter(Boolean)
        .join(" · "),
    };
  }

  if (partnerAccess) {
    return {
      label: "Needs partner access",
      tone: "warning",
      tooltip: buildReviewTooltip(row, reviews) || "Partner feed or permitted access required.",
    };
  }

  if (row?.allowedToIngest === false) {
    return {
      label: "Blocked",
      tone: "disputed",
      tooltip: buildReviewTooltip(row, reviews) || "Not allowed to ingest.",
    };
  }

  if (needsReview) {
    return {
      label: "Needs review",
      tone: "warning",
      tooltip: buildReviewTooltip(row, reviews),
    };
  }

  if (row?.allowedToIngest === true && withLimits) {
    return {
      label: "Allowed with limits",
      tone: "confirmed",
      tooltip:
        buildReviewTooltip(row, reviews) ||
        "Vehicle overlay approved with limits. Automotive license may still be pending.",
    };
  }

  if (row?.allowedToIngest === true) {
    return { label: "Allowed", tone: "confirmed", tooltip: "Compliance gates satisfied." };
  }

  return { label: "Unknown", tone: "neutral", tooltip: "Compliance overlay unavailable." };
}

export function publicDisplayDisplayLabel(row) {
  const value = String(row?.publicDisplayAllowed || "").toLowerCase();
  if (value === "no" || value === "false") {
    const tooltip =
      row?.publicDisplayDisabledReason ||
      (row?.adminImagePreviewValidated
        ? "Public display pending rollout approval. Images are validated internally but public rendering and final approval are pending."
        : "Public display pending counsel/product approval.");
    return {
      label: "Public display pending",
      tooltip,
    };
  }
  if (value === "limited") {
    return {
      label: "Limited public display",
      tooltip: "Public display allowed with documented limits.",
    };
  }
  if (value === "yes" || value === "true") {
    return { label: "Public display approved", tooltip: "Approved for public listing display." };
  }
  return { label: "Unknown", tooltip: "Public display status unavailable." };
}

export function scheduleDisplayLabel(row) {
  if (isVehicleOnlyRow(row) || !row?.capabilities?.supportsSchedule) {
    if (row?.exclusionDisposition === "manual_only" || row?.scheduleAllowed === "no") {
      return "Schedule disabled";
    }
    return "Not supported";
  }
  if (!row?.isActive) {
    return "Disabled";
  }
  if (
    row?.exclusionDisposition === "manual_only" ||
    row?.scheduleAllowed === "no" ||
    !row?.scheduleEnabled
  ) {
    return "Schedule disabled";
  }
  if (row?.scheduleIntervalMinutes == null) {
    return "Manual";
  }
  const match = SCHEDULE_INTERVAL_LABELS.find(
    (item) => item.minutes === row.scheduleIntervalMinutes
  );
  return match?.label || `Every ${row.scheduleIntervalMinutes} min`;
}

export function listingsDisplay(row) {
  if (isVehicleOnlyRow(row)) {
    if (row?.activeListingCount > 0) {
      return row.activeListingCount;
    }
    return "—";
  }
  if (row?.sourceType === "reference" || row?.sourceType === "repair_reference") {
    return row?.lastRecordCount ?? row?.activeListingCount ?? "—";
  }
  return row?.activeListingCount ?? row?.lastRecordCount ?? "—";
}

export function lastRunDisplay(row) {
  if (isVehicleOnlyRow(row)) {
    return { primary: "—", secondary: "No production ingest" };
  }
  return {
    primary: null,
    secondary: row?.lastRunStatus || null,
  };
}

/** Display shape for runs/schedule modals — works for all registry types. */
export function toRunsModalSourceShape(row) {
  if (!row) return null;
  if (row.automotiveSourceKey) {
    return toAutomotivePipelineShape(row);
  }
  return {
    sourceCode: row.vehicleSourceKey || row.id,
    sourceKey: row.vehicleSourceKey || row.id,
    sourceName: row.displayName,
    dataRole: row.dataRole || null,
    isActive: row.isActive,
    enabled: row.isActive,
    scheduleEnabled: false,
    scheduleIntervalMinutes: null,
    nextRunAt: null,
    lastRunAt: null,
    lastStatus: null,
    supportsRunNow: false,
    supportsSchedule: false,
    health: row.healthStatus,
    isRunning: false,
    recordCount: row.activeListingCount ?? null,
    inventoryOnly: true,
  };
}

const RUN_BLOCKED_DISPOSITIONS = new Set([
  "blocked",
  "retired",
  "do_not_use",
  "partner_feed",
  "partner_access_required",
  "deferred",
  "planning_only",
  "inventory_active",
]);

const SCHEDULE_BLOCKED_DISPOSITIONS = new Set([
  ...RUN_BLOCKED_DISPOSITIONS,
  "reference_only",
  "manual_only",
]);

function exclusionDisabledReason(row, action) {
  const disposition = row?.exclusionDisposition;
  if (!disposition) return null;
  if (action === "run" && RUN_BLOCKED_DISPOSITIONS.has(disposition)) {
    return row?.operatorLabel || disposition.replace(/_/g, " ");
  }
  if (action === "schedule" && SCHEDULE_BLOCKED_DISPOSITIONS.has(disposition)) {
    return row?.operatorLabel || disposition.replace(/_/g, " ");
  }
  return null;
}

export function deriveVehicleMarketActionState(row, { canManageSources = true, isBusy = false, isRunning = false } = {}) {
  const vehicleOnly = isVehicleOnlyRow(row);
  const exclusionRunReason = exclusionDisabledReason(row, "run");
  const exclusionScheduleReason = exclusionDisabledReason(row, "schedule");
  const activationReady = row?.activationReady !== false;
  const canRunIngest =
    canManageSources &&
    !vehicleOnly &&
    row?.capabilities?.supportsIngestRun &&
    row?.automotiveSourceKey &&
    activationReady &&
    !exclusionRunReason;
  const canDiscovery = canManageSources && row?.capabilities?.supportsDiscovery;
  const canSchedule =
    !vehicleOnly &&
    canManageSources &&
    row?.capabilities?.supportsSchedule &&
    row?.automotiveSourceKey &&
    activationReady &&
    !exclusionScheduleReason;
  const alreadyScheduled = Boolean(
    !vehicleOnly && row?.scheduleEnabled && row?.automotiveSourceKey && row?.scheduleIntervalMinutes
  );
  const scheduleBlocked =
    vehicleOnly ||
    !canSchedule ||
    Boolean(exclusionScheduleReason) ||
    (row?.scheduleBlocker && row.scheduleBlocker !== "none");

  return {
    runNow: {
      enabled: Boolean(canRunIngest) && !isBusy && !isRunning,
      disabledReason:
        exclusionRunReason ||
        (vehicleOnly
          ? VEHICLE_ONLY_RUN_DISABLED_TOOLTIP
          : !row?.capabilities?.supportsIngestRun
            ? "Ingest not configured"
            : row?.runBlocker && row.runBlocker !== "none"
              ? row.runBlocker
              : null),
    },
    discovery: {
      enabled: Boolean(canDiscovery) && !isBusy,
      visible: vehicleOnly || row?.capabilities?.supportsDiscovery,
      disabledReason: canDiscovery ? null : VEHICLE_ONLY_DISCOVERY_DISABLED_TOOLTIP,
    },
    schedule: {
      enabled: Boolean(canSchedule) && !isBusy && !scheduleBlocked,
      modalAllowed: Boolean(canSchedule || alreadyScheduled) && !vehicleOnly,
      disabledReason: alreadyScheduled
        ? null
        : exclusionScheduleReason ||
          (vehicleOnly
            ? VEHICLE_ONLY_SCHEDULE_DISABLED_TOOLTIP
            : row?.scheduleBlocker && row.scheduleBlocker !== "none"
              ? row.scheduleBlocker
              : null),
    },
    runs: {
      enabled: !isBusy,
      expectsEmptyHistory: vehicleOnly || !row?.automotiveSourceKey,
    },
    details: {
      enabled: !isBusy,
    },
  };
}

export function toAutomotivePipelineShape(row) {
  if (!row?.automotiveSourceKey) return null;
  return {
    sourceCode: row.automotiveSourceKey,
    sourceKey: row.automotiveSourceKey,
    sourceName: row.displayName,
    dataRole: row.dataRole,
    isActive: row.isActive,
    enabled: row.isActive,
    scheduleEnabled: row.scheduleEnabled,
    scheduleIntervalMinutes: row.scheduleIntervalMinutes,
    nextRunAt: row.nextRunAt,
    lastRunAt: row.lastRunAt,
    lastStatus: row.lastRunStatus,
    supportsRunNow: row.capabilities?.supportsIngestRun,
    supportsSchedule: row.capabilities?.supportsSchedule,
    health: row.healthStatus,
    isRunning: row.isRunning,
    recordCount: row.lastRecordCount ?? row.activeListingCount,
  };
}

export function filterVehicleMarketSources(sources, filters = {}) {
  let list = [...(sources || [])];

  if (filters.search) {
    const query = filters.search.toLowerCase();
    list = list.filter((row) => {
      const haystack = [
        row.displayName,
        row.automotiveSourceKey,
        row.vehicleSourceKey,
        row.id,
        row.sourceType,
        row.sourceTypeLabel,
        row.dataRole,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }

  if (filters.typeFilter && filters.typeFilter !== "all") {
    list = list.filter((row) => row.sourceType === filters.typeFilter);
  }

  if (filters.statusFilter && filters.statusFilter !== "all") {
    list = list.filter((row) => {
      const statusKey = normalizeVehicleMarketHealthKey(row);
      if (filters.statusFilter === "inventory_only") {
        return (
          isVehicleOnlyRow(row) &&
          (statusKey === "inventory_only" || statusKey === "inventory_active")
        );
      }
      if (filters.statusFilter === "not_wired") {
        return (
          isVehicleOnlyRow(row) &&
          (statusKey === "needs_ingest_registration" || statusKey === "inventory_only")
        );
      }
      if (filters.statusFilter === "warning") {
        return statusKey === "warning" || statusKey === "stale";
      }
      if (filters.statusFilter === "never_run") {
        return (
          statusKey === "never_run" ||
          statusKey === "unknown" ||
          statusKey === "not_wired" ||
          statusKey === "needs_ingest_registration"
        );
      }
      if (filters.statusFilter === "queued") {
        return statusKey === "queued" || statusKey === "running";
      }
      return statusKey === filters.statusFilter;
    });
  }

  if (filters.enabledFilter === "enabled") {
    list = list.filter((row) => row.isActive !== false);
  } else if (filters.enabledFilter === "disabled") {
    list = list.filter((row) => row.isActive === false);
  }

  if (filters.complianceFilter && filters.complianceFilter !== "all") {
    list = list.filter((row) => {
      const compliance = deriveComplianceDisplay(row);
      if (filters.complianceFilter === "allowed") return compliance.label === "Allowed";
      if (filters.complianceFilter === "needs_review") return compliance.label === "Needs review";
      if (filters.complianceFilter === "blocked") return compliance.label === "Blocked";
      if (filters.complianceFilter === "unknown") return compliance.label === "Unknown";
      return true;
    });
  }

  if (filters.scheduleFilter && filters.scheduleFilter !== "all") {
    list = list.filter((row) => {
      const label = scheduleDisplayLabel(row);
      if (filters.scheduleFilter === "enabled") return row.scheduleEnabled === true;
      if (filters.scheduleFilter === "manual") return label === "Manual";
      if (filters.scheduleFilter === "disabled") return label === "Disabled";
      if (filters.scheduleFilter === "unsupported") return label === "Not supported";
      return true;
    });
  }

  if (filters.registryFilter && filters.registryFilter !== "all") {
    list = list.filter((row) => {
      if (filters.registryFilter === "paired") return row.registry === "paired";
      if (filters.registryFilter === "ingest_only") return row.registry === "automotive_only";
      if (filters.registryFilter === "inventory_only") return row.registry === "vehicle_only";
      return true;
    });
  }

  if (filters.exclusionFilter && filters.exclusionFilter !== "all") {
    list = list.filter((row) => row.exclusionDisposition === filters.exclusionFilter);
  }

  return list.sort((a, b) => String(a.displayName).localeCompare(String(b.displayName)));
}
